// Step 3 of the unscheduled-visit flow (KAN-765): the Log Job page, entered
// from the full-screen flow, wears the SAME header component as steps 1-2 — constant
// title, the stepper on "Log the visit", a back chevron and a ✕ — so the
// screen reads as one flow even though Log Job is its own route (it stays one
// for the keyboard / AI-context / MFA rules that key off its pathname).
//
//   Back  -> returns to the board and reopens the flow on the Reason step
//            (customer + reason kept; see unscheduledFlowShared.js).
//   ✕     -> leaves for the board. Both ways out ask "Unsaved changes — Stay /
//            Leave" once the form has anything in it; a save in flight blocks both.
import { useCallback } from 'react';
import { useUnsavedChangesGuard } from '../../../hooks/ui/useUnsavedChangesGuard';
import UnscheduledFlowHeader from './UnscheduledFlowHeader';
import { buildResumeState } from './unscheduledFlowShared';

/**
 * @param {object} props
 * @param {'phone'|'tablet'|'desktop'} props.layout
 * @param {string} props.customerId
 * @param {string} props.reason
 * @param {boolean} props.sameDayOverride
 * @param {string} props.returnTo - the board this was launched from.
 * @param {(to: string, opts?: object) => void} props.navigate
 * @param {() => boolean} [props.isDirty] - the form has something in it.
 * @param {() => boolean} [props.isBusy] - a save is running.
 */
export default function UnscheduledLogJobHeader({
  layout, customerId, reason, sameDayOverride, returnTo, navigate, isDirty = () => false, isBusy = () => false,
}) {
  const leave = useCallback(() => navigate(returnTo, { replace: true }), [navigate, returnTo]);
  const guard = useUnsavedChangesGuard({ isDirty, isBusy, onClose: leave });
  const goBackToReason = useCallback(() => {
    navigate(returnTo, { replace: true, state: buildResumeState({ customerId, reason, sameDayOverride }) });
  }, [navigate, returnTo, customerId, reason, sameDayOverride]);

  return (
    <>
      <UnscheduledFlowHeader
        layout={layout}
        currentStep={3}
        onBack={() => guard.runGuarded(goBackToReason)}
        onClose={guard.guardedClose}
        testId="unscheduled-logjob-header"
      />
      {guard.confirmNode}
    </>
  );
}
